import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Grid,
  Row,
  Space,
  Tag,
  theme,
  Typography,
} from 'antd';
import Tabs from '../common/LegacyTabs';
import { withDevModalFilePath, UserDisplay, EquipmentDisplay, WorkOrderDisplay } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/WorkSchedule/DetailModal.tsx');
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  CopyOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { trpc } from '../../lib/api';
import type {
  WorkScheduleStatus,
  WorkScheduleResult,
  ChecklistResults,
} from '../../../../shared/types/work-schedule.types';
import { WorkScheduleExecuteModal } from './ExecuteModal';
import { CompleteWorkModal } from './CompleteWorkModal';
import { ApprovalButton } from './ApprovalButton';
import { useAuthStore } from '../../stores/authStore';
import type { ScheduleSelectedEquipment } from '../../types/work-schedule';
import { ChecklistProgress } from '../work-order';
import { useWorkOrderTemplate } from '../../hooks/useWorkOrderTemplates';
import type { ChecklistItem } from '../../../../shared/types/work-order-template.types';
import { FileGallery } from '../file/FileGallery';
import { ScheduleCommentList } from '../schedule';
import { getMutationPendingState } from '../../utils/trpcMutation';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

type ChecklistResult = {
  itemId: string;
  completed: boolean;
  value?: string | number | boolean;
  notes?: string;
  completedAt?: string | Date;
  completedBy?: string;
};

type EquipmentSummary = {
  id: string;
  name?: string | null;
  displayId?: string | null;
  code?: string | null;
};

type UserSummary = {
  id: string;
  name?: string | null;
  email?: string | null;
};

export interface WorkScheduleDetail {
  id: string;
  companyId?: string;
  displayId?: string;
  title?: string;
  description?: string | null;
  workType?: string | null;
  workTypeCategoryId?: string | null;
  workTypeCategory?: {
    id: string;
    categoryLabel: string;
    color?: string | null;
    icon?: string | null;
  } | null;
  priority?: string | null;
  status: WorkScheduleStatus;
  scheduledDate?: string | Date;
  scheduledDuration?: number | null;
  scheduleType?: string | null;
  parentScheduleId?: string | null;
  orderIndex?: number | null;
  actualStart?: string | Date | null;
  actualEnd?: string | Date | null;
  actualDuration?: number | null;
  assignedTo?: string | null;
  assignedUsers?: Array<{
    id: string;
    name?: string | null;
    email?: string | null;
    avatarUrl?: string | null;
    preferences?: Record<string, unknown> | null;
    role?: string | null;
    assignedAt?: Date | null;
  }>;
  workOrderTemplateId?: string | null;
  workPerformed?: string | null;
  findings?: string | null;
  recommendations?: string | null;
  result?: WorkScheduleResult | null;
  checklistResults?: ChecklistResults | ChecklistResult[] | null;
  requiresApproval?: boolean;
  approverUserIds?: string[];
  recurrenceRule?: {
    enabled: boolean;
    pattern: string;
    interval: number;
    endDate?: string | Date | null;
    includeChildren?: boolean;
    advanceDays?: number;
    daysOfWeek?: number[];
    dayOfMonth?: number;
    monthsOfYear?: number[];
  } | null;
  selectedEquipments?: ScheduleSelectedEquipment[] | null;
  createdBy?: string;
  updatedAt?: string | Date;
}

interface WorkScheduleDetailModalProps {
  open: boolean;
  schedule: WorkScheduleDetail | null;
  onClose: () => void;
  onEdit?: (schedule: WorkScheduleDetail) => void;
  onChanged?: () => Promise<unknown> | unknown;
  onDeleted?: () => Promise<unknown> | unknown;
}

const toChecklistResults = (
  value: WorkScheduleDetail['checklistResults'],
): ChecklistResults | undefined => {
  if (!value || Array.isArray(value)) {
    return undefined;
  }

  const candidate = value as Partial<ChecklistResults>;
  return Array.isArray(candidate.items) && typeof candidate.completionRate === 'number'
    ? (value as ChecklistResults)
    : undefined;
};

const STATUS_META: Record<
  WorkScheduleStatus,
  { color: string; label: string; icon: JSX.Element }
> = {
  scheduled: { color: 'purple', label: '予定', icon: <ScheduleOutlined /> },
  in_progress: { color: 'blue', label: '実行中', icon: <PlayCircleOutlined /> },
  pending_approval: { color: 'orange', label: '承認待ち', icon: <ClockCircleOutlined /> },
  completed: { color: 'green', label: '完了', icon: <CheckCircleOutlined /> },
  cancelled: { color: 'red', label: 'キャンセル', icon: <InfoCircleOutlined /> },
  postponed: { color: 'orange', label: '延期', icon: <CalendarOutlined /> }
};

// 優先度カラー（将来の使用に備えて定義）
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _PRIORITY_COLOR: Record<string, string> = {
  urgent: 'red',
  high: 'orange',
  medium: 'blue',
  low: 'green'
};

const WORK_TYPE_LABEL: Record<string, string> = {
  inspection: '点検',
  repair: '修理',
  replacement: '交換',
  maintenance: 'メンテナンス'
};

function formatDate(value?: string | Date | null, format = 'YYYY/MM/DD HH:mm'): string {
  if (!value) {
    return '-';
  }
  return moment(value).isValid() ? moment(value).format(format) : '-';
}

function formatDuration(minutes?: number | null): string {
  if (!minutes) {
    return '-';
  }
  if (minutes < 60) {
    return `${minutes} 分`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} 時間 ${rest} 分` : `${hours} 時間`;
}

export function WorkScheduleDetailModal({
  open,
  schedule,
  onClose,
  onEdit,
  onChanged,
  onDeleted: _onDeleted
}: WorkScheduleDetailModalProps): JSX.Element {
  const navigate = useNavigate();
  const utils = trpc.useContext();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [executeOpen, setExecuteOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const { user } = useAuthStore();
  const effectiveCompanyId = schedule?.companyId ?? user?.companyId ?? undefined;
  const screens = Grid.useBreakpoint();
  // レスポンシブ対応用 - 現在はDescriptionsのcolumnで直接使用
  void (screens.md || screens.lg || screens.xl || screens.xxl ? 2 : 1);

  // テンプレートからチェックリスト項目を取得
  const { template } = useWorkOrderTemplate(
    schedule?.workOrderTemplateId ?? null,
    { includeEquipments: false }
  );

  const equipmentQueryInput = useMemo(() => {
    const base: { companyId?: string; page: number; limit: number } = {
      page: 1,
      limit: 200
    };
    if (effectiveCompanyId) {
      base.companyId = effectiveCompanyId;
    }
    return base;
  }, [effectiveCompanyId]);

  const userQueryInput = useMemo(() => {
    const base: { companyId?: string; page: number; limit: number } = {
      page: 1,
      limit: 200
    };
    if (effectiveCompanyId) {
      base.companyId = effectiveCompanyId;
    }
    return base;
  }, [effectiveCompanyId]);

  const { data: equipmentQueryResult } = trpc.equipment.list.useQuery(equipmentQueryInput, {
    enabled: Boolean(effectiveCompanyId)
  });

  const { data: userQueryResult } = trpc.user.listDisplay.useQuery(
    userQueryInput,
    {
    enabled: Boolean(effectiveCompanyId)
    }
  );

  const equipmentList = useMemo<EquipmentSummary[]>(() => {
    if (!equipmentQueryResult) {
      return [];
    }
    const fromData = (equipmentQueryResult as { data?: EquipmentSummary[] }).data;
    if (Array.isArray(fromData)) {
      return fromData;
    }
    const fromItems = (equipmentQueryResult as { items?: EquipmentSummary[] }).items;
    if (Array.isArray(fromItems)) {
      return fromItems;
    }
    return [];
  }, [equipmentQueryResult]);

  const getEquipmentLabel = (equipment: EquipmentSummary) =>
    equipment.name ?? equipment.displayId ?? equipment.code ?? equipment.id;

  const equipmentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const equipment of equipmentList) {
      if (equipment?.id) {
        map.set(equipment.id, getEquipmentLabel(equipment));
      }
    }
    return map;
  }, [equipmentList]);

  const userList = useMemo<UserSummary[]>(() => {
    if (!userQueryResult) {
      return [];
    }
    const fromData = (userQueryResult as { data?: UserSummary[] }).data;
    if (Array.isArray(fromData)) {
      return fromData;
    }
    const fromItems = (userQueryResult as { items?: UserSummary[] }).items;
    if (Array.isArray(fromItems)) {
      return fromItems;
    }
    return [];
  }, [userQueryResult]);

  const getUserLabel = (userItem: UserSummary) =>
    userItem.name ?? userItem.email ?? userItem.id;

  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const userItem of userList) {
      if (userItem?.id) {
        map.set(userItem.id, getUserLabel(userItem));
      }
    }
    return map;
  }, [userList]);

  const resolveEquipmentName = (equipmentId: string, fallbackName?: string | null) =>
    fallbackName ?? equipmentNameMap.get(equipmentId) ?? equipmentId;

  const assignedUsersDisplay = useMemo(() => {
    // 1. assignedUsers配列がある場合（workScheduleAssignmentsテーブルから取得）
    if (schedule?.assignedUsers && schedule.assignedUsers.length > 0) {
      return (
        <Space size="small" wrap>
          {schedule.assignedUsers.map((u) => (
            <UserDisplay
              key={u.id}
              userId={u.id}
              user={u}
              size="small"
            />
          ))}
        </Space>
      );
    }
    // 2. assignedToフィールドがある場合（レガシーサポート）
    if (schedule?.assignedTo) {
      const fallbackName = userNameMap.get(schedule.assignedTo);
      return (
        <UserDisplay
          userId={schedule.assignedTo}
          user={fallbackName ? { id: schedule.assignedTo, name: fallbackName } : undefined}
          size="small"
        />
      );
    }
    return <Text type="secondary">担当未設定</Text>;
  }, [schedule?.assignedUsers, schedule?.assignedTo, userNameMap]);

  const startWork = trpc.schedule.startWork.useMutation({
    onSuccess: async () => {
      message.success('作業を開始しました');
      await utils.schedule.getCalendarView.invalidate();
      if (onChanged) {
        await onChanged();
      }
    },
    onError: (error) => {
      message.error(error.message || '作業開始に失敗しました');
    }
  });

  const duplicateSchedule = trpc.schedule.duplicate.useMutation({
    onSuccess: async (data) => {
      message.success(`スケジュールを複製しました: ${data.title}`);
      await utils.schedule.getCalendarView.invalidate();
      if (onChanged) {
        await onChanged();
      }
      // 複製したスケジュールの詳細ページへ遷移
      navigate(`/app/schedule/${data.id}`);
      onClose();
    },
    onError: (error) => {
      message.error(error.message || 'スケジュールの複製に失敗しました');
    }
  });

  const canStart = useMemo(() => {
    if (!schedule) {
      return false;
    }
    return schedule.status === 'scheduled' || schedule.status === 'postponed';
  }, [schedule]);

  const handleStart = () => {
    if (!schedule) {
      return;
    }
    startWork.mutate({
      id: schedule.id,
      actualStart: new Date()
    });
  };


  const handleEdit = () => {
    if (schedule && onEdit) {
      onEdit(schedule);
    }
  };

  const handleExecuteCompleted = async () => {
    await onChanged?.();
    setExecuteOpen(false);
  };

  const handleCompleteWorkCompleted = async () => {
    await onChanged?.();
    setCompleteModalOpen(false);
  };

  const handleApprovalChanged = async () => {
    await onChanged?.();
  };

  const handleOpenDetailPage = () => {
    if (schedule?.id) {
      navigate(`/app/schedule/${schedule.id}`);
      onClose();
    }
  };

  const footerButtons = [
    // 基本アクション
    <Button key="close" onClick={onClose}>
      閉じる
    </Button>,
    <Button
      key="detail"
      icon={<FileTextOutlined />}
      onClick={handleOpenDetailPage}
      disabled={!schedule}
    >
      詳細ページを開く
    </Button>,
    <Button
      key="edit"
      icon={<EditOutlined />}
      onClick={handleEdit}
      disabled={!schedule || !onEdit}
    >
      編集
    </Button>,
    <Button
      key="duplicate"
      icon={<CopyOutlined />}
      onClick={() => schedule && duplicateSchedule.mutate({ id: schedule.id })}
      disabled={!schedule}
      loading={getMutationPendingState(duplicateSchedule)}
    >
      複製
    </Button>,
    // 作業関連アクション
    <Button
      key="execute"
      icon={<CheckSquareOutlined />}
      onClick={() => setExecuteOpen(true)}
      disabled={!schedule || schedule.status === 'completed'}
    >
      作業報告
    </Button>,
    // プライマリアクション
    schedule?.status === 'in_progress' && (
      <Button
        key="complete"
        type="primary"
        icon={<CheckCircleOutlined />}
        onClick={() => setCompleteModalOpen(true)}
      >
        完了報告
      </Button>
    ),
    canStart && (
      <Button
        key="start"
        type="primary"
        icon={<PlayCircleOutlined />}
        onClick={handleStart}
        loading={getMutationPendingState(startWork)}
      >
        作業開始
      </Button>
    )
  ].filter(Boolean);

  const checklistSummary = toChecklistResults(schedule?.checklistResults ?? null);
  const selectedEquipments = schedule?.selectedEquipments ?? [];
  const workOrderTemplateDisplay = template?.id
    ? {
        id: template.id,
        templateName: template.templateName ?? null,
        displayId: template.displayId ?? null,
        workTypeCategoryId: template.workTypeCategoryId ?? null,
        workTypeCategory: template.workTypeCategory
          ? {
              categoryLabel: template.workTypeCategory.categoryLabel ?? null,
              color: template.workTypeCategory.color ?? null,
            }
          : null,
        priority: template.priority ?? null,
        standardHours: template.standardHours ?? null,
      }
    : null;

  const statusMeta = schedule ? STATUS_META[schedule.status] : null;

  // 承認タブの表示条件
  const showApprovalTab = schedule?.requiresApproval ||
    (schedule?.approverUserIds && schedule.approverUserIds.length > 0);

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          <span>スケジュール詳細</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={footerButtons}
      width={960}
      destroyOnHidden
    >
      {schedule ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* ヘッダー: タイトル・ステータス・5W1H */}
          <div style={{
            background: token.colorBgElevated,
            borderRadius: 8,
            padding: 16,
            border: `1px solid ${token.colorBorderSecondary}`,
            color: token.colorText
          }}>
            {/* タイトルとステータスタグ */}
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <Title level={4} style={{ margin: 0 }}>
                  {schedule.title || '名称未設定のスケジュール'}
                </Title>
                <Space size="small" wrap>
                  {schedule.displayId && <Tag color="geekblue">#{schedule.displayId}</Tag>}
                  {statusMeta && (
                    <Tag color={statusMeta.color} icon={statusMeta.icon}>
                      {statusMeta.label}
                    </Tag>
                  )}
                </Space>
              </div>

              {/* 誰が・いつ・どこで・何を */}
              <Descriptions
                size="small"
                column={{ xs: 1, sm: 2, md: 4 }}
                style={{ marginTop: 8 }}
              >
                <Descriptions.Item label={<><ClockCircleOutlined /> いつ</>}>
                  <Text strong>{formatDate(schedule.scheduledDate, 'MM/DD HH:mm')}</Text>
                  {schedule.scheduledDuration && (
                    <Text type="secondary" style={{ marginLeft: 4 }}>
                      ({formatDuration(schedule.scheduledDuration)})
                    </Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={<><ToolOutlined /> 何を</>}>
                  {schedule.workType ? (
                    <Tag color="default">
                      {WORK_TYPE_LABEL[schedule.workType] ?? schedule.workType}
                    </Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="どこで">
                  {selectedEquipments.length > 0 ? (
                    (() => {
                      const item = selectedEquipments[0];
                      const equipment = equipmentList.find(e => e.id === item.equipmentId);
                      const displayName = item.equipmentName ?? equipment?.name ?? resolveEquipmentName(item.equipmentId, item.equipmentName);
                      return (
                        <EquipmentDisplay
                          equipment={{
                            id: item.equipmentId,
                            displayId: equipment?.displayId || equipment?.code || '',
                            name: displayName,
                          }}
                        />
                      );
                    })()
                  ) : (
                    <Text type="secondary">未設定</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="誰が">
                  {assignedUsersDisplay}
                </Descriptions.Item>
              </Descriptions>
            </Space>
          </div>

          {/* タブコンテンツ */}
          <Tabs defaultActiveKey="overview">
            {/* 概要タブ（基本情報＋対象設備・作業実績をマージ） */}
            <TabPane tab="概要" key="overview">
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* 説明 */}
                {schedule.description && (
                  <Alert
                    message="説明"
                    description={schedule.description}
                    type="info"
                    showIcon
                  />
                )}

                {/* 作業指示書（設定されている場合のみ） */}
                {schedule.workOrderTemplateId && workOrderTemplateDisplay && (
                  <Card size="small" style={{ background: token.colorSuccessBg, border: `1px solid ${token.colorSuccessBorder}` }}>
                    <Space>
                      <FileTextOutlined style={{ color: token.colorSuccess }} />
                      <Text strong>作業指示書:</Text>
                      <WorkOrderDisplay workOrderTemplate={workOrderTemplateDisplay} />
                    </Space>
                  </Card>
                )}

                {/* 実績時間 + チェックリスト（2カラム） */}
                <Row gutter={[16, 16]}>
                  {(schedule.actualStart || schedule.actualEnd || schedule.actualDuration) && (
                    <Col xs={24} md={12}>
                      <Card title="実績時間" size="small" style={{ height: '100%' }}>
                        <Descriptions bordered column={1} size="small">
                          {schedule.actualStart && (
                            <Descriptions.Item label="開始">
                              <Space>
                                <ClockCircleOutlined />
                                <Text>{formatDate(schedule.actualStart)}</Text>
                              </Space>
                            </Descriptions.Item>
                          )}
                          {schedule.actualEnd && (
                            <Descriptions.Item label="終了">
                              <Space>
                                <ClockCircleOutlined />
                                <Text>{formatDate(schedule.actualEnd)}</Text>
                              </Space>
                            </Descriptions.Item>
                          )}
                          {schedule.actualDuration !== undefined && schedule.actualDuration !== null && (
                            <Descriptions.Item label="所要時間">
                              {formatDuration(schedule.actualDuration)}
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      </Card>
                    </Col>
                  )}
                  <Col xs={24} md={(schedule.actualStart || schedule.actualEnd || schedule.actualDuration) ? 12 : 24}>
                    <Card title="チェックリスト" size="small" style={{ height: '100%' }}>
                      {template?.checklistItems && (template.checklistItems as ChecklistItem[]).length > 0 ? (
                        <ChecklistProgress
                          templateItems={(template.checklistItems as ChecklistItem[]) || []}
                          results={checklistSummary}
                        />
                      ) : (
                        <Text type="secondary">チェックリストが設定されていません</Text>
                      )}
                    </Card>
                  </Col>
                </Row>

                {/* 下段: 作業報告（全幅） */}
                {(schedule.workPerformed || schedule.findings || schedule.recommendations || schedule.result) && (
                  <Card title="作業報告" size="small">
                    <Row gutter={[16, 12]}>
                      {schedule.result && (
                        <Col span={24}>
                          <Space>
                            <Text strong>作業結果:</Text>
                            <Tag color={
                              schedule.result === 'good' ? 'green' :
                              schedule.result === 'attention_needed' ? 'orange' :
                              schedule.result === 'repair_required' ? 'red' : 'default'
                            }>
                              {schedule.result === 'good' ? '良好' :
                               schedule.result === 'attention_needed' ? '要注意' :
                               schedule.result === 'repair_required' ? '修理必要' : schedule.result}
                            </Tag>
                          </Space>
                        </Col>
                      )}
                      {schedule.workPerformed && (
                        <Col xs={24} md={12}>
                          <Text strong>作業内容:</Text>
                          <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', color: token.colorText }}>
                            {schedule.workPerformed}
                          </div>
                        </Col>
                      )}
                      {schedule.findings && (
                        <Col xs={24} md={12}>
                          <Text strong>発見事項:</Text>
                          <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', color: token.colorText }}>
                            {schedule.findings}
                          </div>
                        </Col>
                      )}
                      {schedule.recommendations && (
                        <Col span={24}>
                          <Text strong>推奨事項:</Text>
                          <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', color: token.colorText }}>
                            {schedule.recommendations}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Card>
                )}

                {/* 繰り返し設定（条件付き） */}
                {schedule.recurrenceRule?.enabled && (
                  <Alert
                    type="success"
                    icon={<SafetyCertificateOutlined />}
                    message="繰り返し設定"
                    description={
                      <Space direction="vertical" size={4}>
                        <Text>
                          パターン: {schedule.recurrenceRule.pattern} / 間隔: {schedule.recurrenceRule.interval}
                        </Text>
                        <Text>
                          終了日:{' '}
                          {schedule.recurrenceRule.endDate
                            ? formatDate(schedule.recurrenceRule.endDate, 'YYYY/MM/DD')
                            : '未設定'}
                        </Text>
                      </Space>
                    }
                  />
                )}

                {/* 最終更新 */}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  最終更新: {formatDate(schedule.updatedAt)}
                </Text>
              </Space>
            </TabPane>

            {/* 承認・設定タブ（条件付き表示） */}
            {showApprovalTab && (
              <TabPane tab="承認・設定" key="approval">
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  {/* 承認ワークフロー設定 */}
                  <Card title="承認ワークフロー設定" size="small">
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div>
                        <Text strong>完了時に承認が必要:</Text>
                        {' '}
                        {schedule.requiresApproval ? (
                          <Tag color="orange">必要</Tag>
                        ) : (
                          <Tag color="default">不要</Tag>
                        )}
                      </div>
                      {schedule.approverUserIds && schedule.approverUserIds.length > 0 && (
                        <div>
                          <Text strong>承認者:</Text>
                          <br />
                          <Space wrap style={{ marginTop: 4 }}>
                            {schedule.approverUserIds.map((userId) => (
                              <Tag key={userId} color="blue">
                                {userNameMap.get(userId) ?? userId}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      )}
                    </Space>
                  </Card>

                  {/* 承認ボタン */}
                  {schedule.status === 'pending_approval' && (
                    <Card title="承認" size="small">
                      <ApprovalButton
                        schedule={schedule}
                        onApproved={handleApprovalChanged}
                        onRejected={handleApprovalChanged}
                      />
                    </Card>
                  )}
                </Space>
              </TabPane>
            )}

            {/* 作業記録タブ */}
            <TabPane tab="作業記録" key="files">
              <Card title="作業記録・写真" size="small">
                <FileGallery
                  entityType="schedule"
                  entityId={schedule.id}
                  category="work_record"
                  editable={schedule.status !== 'cancelled'}
                  maxFiles={30}
                  maxSize={10}
                  accept="image/*,video/*"
                  enableCamera={true}
                />
              </Card>
            </TabPane>

            {/* コメントタブ */}
            <TabPane tab={<span><CommentOutlined /> コメント</span>} key="comments">
              <ScheduleCommentList
                scheduleId={schedule.id}
                editable={schedule.status !== 'cancelled'}
                currentStatus={schedule.status}
                onStatusChange={() => void onChanged?.()}
              />
            </TabPane>
          </Tabs>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }} size={16}>
          <InfoCircleOutlined style={{ fontSize: 36, color: token.colorTextQuaternary }} />
          <Text type="secondary">スケジュール情報が見つかりません。</Text>
        </Space>
      )}
      <WorkScheduleExecuteModal
        open={executeOpen}
        schedule={schedule}
        onClose={() => setExecuteOpen(false)}
        onCompleted={handleExecuteCompleted}
      />

      <CompleteWorkModal
        open={completeModalOpen}
        schedule={schedule}
        onClose={() => setCompleteModalOpen(false)}
        onCompleted={handleCompleteWorkCompleted}
      />

    </Modal>
  );
}
